import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, primeFetch } from '../App';
import { todayNPT, yesterdayNPT } from '../utils/neptime';
import './Login.css';

const ROLES = [
  { id: 'owner',   label: 'Owner'   },
  { id: 'manager', label: 'Manager' },
  { id: 'staff',   label: 'Staff'   },
];

const ROLE_ICONS = {
  owner: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11.5L12 4l9 7.5"/>
      <path d="M5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9"/>
      <path d="M10 20v-6h4v6"/>
    </svg>
  ),
  manager: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9.5C7.5 20.5 4 17 4 12V6l8-3z"/>
    </svg>
  ),
  staff: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 2v8a2 2 0 0 0 2 2v9M7 2v8a2 2 0 0 1-2 2v9M11 2v8M15 2c-1 0-3 1-3 5s2 5 3 5v9"/>
    </svg>
  ),
};

// Feature pills — six editorial pills below the headline. Mix of feature
// descriptions and brand qualities. Order matters: feature → feature → feature
// → feature → quality → quality, building from concrete to abstract.
const PILLS = [
  'Orders, in a tap',
  'Menus in minutes',
  'Tables at a glance',
  'AI Daily Insights',
  'Quiet by design',
  'Privacy by default',
];

// Editorial marquee — generic, humorous, restaurant-life one-liners that rotate
// every ~7 s in the noir panel's bottom slot. Pre-auth decorative content; tone
// is light, no attribution, designed to make a returning owner smile.
// One word in each quote is wrapped in <em> to receive the saffron accent.
const QUOTES = [
  ['"Coffee was the only ', 'employee', ' on time today."'],
  ['"Every order is urgent. ', 'Every. Single. One.', '"'],
  ['"Today’s special is ', 'whatever', ' runs out last."'],
  ['"The till closes at midnight. The ', 'thinking', ' doesn’t."'],
  ['"Half the menu, ', 'double', ' the love."'],
  ['"Tables don’t tip themselves. But ', 'they could try.', '"'],
  ['"A good night ends with empty plates and ', 'full chairs.', '"'],
  ['"The receipt printer has more ', 'loyalty', ' than most exes."'],
];

function VPMonogram({ size = 240 }) {
  const markSize = Math.round(size * 0.55);
  const idSuffix = `vp-${size}`;
  return (
    <div className="vp-monogram" style={{ width: size, height: size }}>
      <div className="vp-monogram-halo"></div>
      <svg className="vp-monogram-guides" width={size} height={size} viewBox="0 0 200 200">
        <defs>
          <radialGradient id={`constMask-${idSuffix}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.9"/>
            <stop offset="60%" stopColor="#fff" stopOpacity="0.4"/>
            <stop offset="100%" stopColor="#fff" stopOpacity="0"/>
          </radialGradient>
          <mask id={`constM-${idSuffix}`}><rect width="200" height="200" fill={`url(#constMask-${idSuffix})`}/></mask>
        </defs>
        <g mask={`url(#constM-${idSuffix})`} stroke="#F5F7FB" fill="none" strokeWidth="0.4" opacity="0.45">
          <circle cx="100" cy="100" r="90"/>
          <circle cx="100" cy="100" r="74" strokeDasharray="0.5 4"/>
          <circle cx="100" cy="100" r="58"/>
          <circle cx="100" cy="100" r="42" strokeDasharray="0.5 3"/>
          {Array.from({ length: 24 }).map((_, i) => {
            const a = ((i * 15 - 90) * Math.PI) / 180;
            const inner = i % 6 === 0 ? 84 : 87;
            const outer = i % 6 === 0 ? 92 : 90;
            return (
              <line
                key={i}
                x1={100 + Math.cos(a) * inner}
                y1={100 + Math.sin(a) * inner}
                x2={100 + Math.cos(a) * outer}
                y2={100 + Math.sin(a) * outer}
                stroke={i % 6 === 0 ? '#D4A537' : '#F5F7FB'}
                strokeWidth={i % 6 === 0 ? 0.8 : 0.4}
                opacity={i % 6 === 0 ? 0.9 : 0.5}
              />
            );
          })}
          <line x1="100" y1="72" x2="100" y2="128" strokeWidth="0.3" opacity="0.4"/>
          <line x1="80.2" y1="80.2" x2="119.8" y2="119.8" strokeWidth="0.3" opacity="0.4"/>
          <line x1="72" y1="100" x2="128" y2="100" strokeWidth="0.3" opacity="0.4"/>
          <line x1="80.2" y1="119.8" x2="119.8" y2="80.2" strokeWidth="0.3" opacity="0.4"/>
        </g>
        <g style={{ animation: 'vpSweep 4.5s cubic-bezier(0.4,0,0.2,1) infinite', transformOrigin: '100px 100px' }}>
          <path d="M 100 14 A 86 86 0 0 1 168 52" stroke="#D4A537" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.85"/>
        </g>
      </svg>
      <svg className="vp-monogram-mark" width={markSize} height={markSize} viewBox="0 0 100 100">
        <defs>
          <linearGradient id={`vpGold-${idSuffix}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E5BD52"/>
            <stop offset="50%" stopColor="#D4A537"/>
            <stop offset="100%" stopColor="#8A6A1A"/>
          </linearGradient>
          <linearGradient id={`vpInk-${idSuffix}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#F5F7FB" stopOpacity="0.95"/>
            <stop offset="100%" stopColor="#F5F7FB" stopOpacity="0.6"/>
          </linearGradient>
        </defs>
        <path d="M 18 18 L 42 78 L 50 78 L 32 18 Z" fill={`url(#vpGold-${idSuffix})`} stroke="#E5BD52" strokeWidth="0.3"/>
        <path d="M 50 78 L 56 78 L 76 18 L 64 18 Z" fill={`url(#vpInk-${idSuffix})`} opacity="0.9"/>
        <path
          d="M 56 22 L 68 22 Q 84 22 84 36 Q 84 50 68 50 L 62 50 L 62 64 M 62 32 L 68 32 Q 74 32 74 36 Q 74 40 68 40 L 62 40 Z"
          fill="none" stroke={`url(#vpInk-${idSuffix})`} strokeWidth="3.5" strokeLinejoin="round" strokeLinecap="round"
        />
        <circle cx="49" cy="84" r="1.8" fill="#D4A537"/>
        {size >= 200 && <circle cx="49" cy="84" r="0.7" fill="#F5F7FB"/>}
      </svg>
    </div>
  );
}

function Wordmark() {
  return (
    <div className="vp-wordmark">
      <svg className="vp-wordmark-mark" viewBox="0 0 24 24"><path d="M3 3 L11 21 L13 21 L21 3 L17 3 L12 14 L7 3 Z" fill="#D4A537"/></svg>
      <div>
        <div className="vp-wordmark-text">Vyapar <span className="pro">Pro</span></div>
        <div className="vp-wordmark-tagline">व्यापार <span className="pro">Pro</span></div>
      </div>
    </div>
  );
}

function StampLine({ time, location = 'KATHMANDU', date }) {
  return (
    <div className="vp-stamp-line">
      <span className="dot"></span>
      <span>{time}</span>
      {location && <><span className="sep">·</span><span>{location}</span></>}
      {date && <><span className="sep">·</span><span>{date}</span></>}
    </div>
  );
}

function useNowStamp() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  const time = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  const date = now.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).toUpperCase();
  return { time, date };
}

export default function Login() {
  const [role, setRole]                     = useState('owner');
  const [restaurantCode, setRestaurantCode] = useState('');
  const [username, setUsername]             = useState('');
  const [password, setPassword]             = useState('');
  const [showPassword, setShowPassword]     = useState(false);
  const [remember, setRemember]             = useState(true);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState('');
  // Mobile-only sign-in animation: idle → scanning → success → entering (or error).
  const [phase, setPhase]                   = useState('idle');
  const { login } = useAuth();
  const navigate = useNavigate();
  const { time, date } = useNowStamp();

  const allFilled = !!(restaurantCode.trim() && username.trim() && password.trim());

  // First-load cold-path warmups, fired as soon as Login mounts so they run
  // in parallel with the user typing their credentials:
  //   1. Hit /api/health so the Vercel serverless function is warm by the
  //      time they submit — kills the typical 300-1000 ms cold-start tax
  //      from the first /dashboard request.
  //   2. Prefetch the heavy authenticated route chunks (Dashboard,
  //      OrderTaking, OrderHistory). When react-router resolves the lazy
  //      route after login, the chunk is already in cache, so the page
  //      mounts immediately instead of waiting on a chunk download over
  //      slow 4G. webpack dedupes — these don't re-fetch later.
  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    fetch(`${API_BASE}/health`).catch(() => {});
    import('./Dashboard');
    import('./OrderTaking');
    import('./OrderHistory');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const reduce   = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const wait = (ms) => new Promise(r => setTimeout(r, ms));

    setLoading(true);
    setError('');
    if (isMobile) setPhase('scanning');
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurant_code: restaurantCode, username, password, remember }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');

      // Race the route transition by kicking off /dashboard right now.
      // primeFetch registers the promise in the shared _inflightMap so
      // Dashboard.loadData()'s api.get() sees an in-flight request for
      // the same path and rides on it instead of firing a duplicate.
      // Fire-and-forget — failure just means the consumer falls back
      // to its normal fetch path on a cache miss. Safe to call before
      // login() — it only needs the token, not the auth state.
      try {
        const today = todayNPT();
        const yesterday = yesterdayNPT();
        primeFetch(data.token, `/dashboard?date=${today}&yesterday=${yesterday}`);
      } catch (_) { /* never block the navigation on this */ }

      // Mobile: play the fingerprint-unlock → navy-wipe sequence over the
      // (already in-flight) dashboard load, BEFORE flipping auth. login()
      // sets the user, which makes the /login route redirect to /dashboard
      // and unmount this page — so it must come last, once the navy overlay
      // is already covering the screen.
      if (isMobile && !reduce) {
        setPhase('success');   // ridges → checkmark, ring pops
        await wait(520);
        setPhase('entering');  // navy overlay wipes up
        await wait(480);
      }

      login(data.user, data.token);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
      setLoading(false);
      if (isMobile) {
        setPhase('error');     // ring shakes red, then back to idle
        setTimeout(() => setPhase('idle'), 600);
      }
    }
  };

  /* ─── Form (shared between desktop card + mobile sheet) ─── */
  const formBlock = (
    <form onSubmit={handleSubmit} className="login-form-stack">
      <div>
        <div className="login-eyebrow">Sign in to your workspace</div>
        <h1 className="login-h1">Welcome back</h1>
        <p className="login-sub">Sign in to manage your restaurant.</p>
      </div>

      {error && <div className="login-error">{error}</div>}

      <div className="role-chips">
        {ROLES.map(r => (
          <button
            key={r.id}
            type="button"
            className={`role-chip${role === r.id ? ' active' : ''}`}
            onClick={() => { setRole(r.id); setError(''); }}
          >
            {ROLE_ICONS[r.id]}
            <span>{r.label}</span>
          </button>
        ))}
      </div>

      <div className="login-fields">
        <div className="field">
          <label className="field-label">Restaurant code</label>
          <div className="field-input">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#857B6D" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="3" width="16" height="18" rx="1.5"/>
              <path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2"/>
              <path d="M10 21v-3h4v3"/>
            </svg>
            <input
              type="text"
              value={restaurantCode}
              onChange={e => setRestaurantCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              placeholder="e.g. himalayan"
              autoCapitalize="none"
              autoCorrect="off"
              required
            />
          </div>
        </div>

        <div className="field">
          <label className="field-label">Username</label>
          <div className="field-input">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#857B6D" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4"/>
              <path d="M4 21a8 8 0 0 1 16 0"/>
            </svg>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="your_username"
              autoCapitalize="none"
              autoCorrect="off"
              required
            />
          </div>
        </div>

        <div className="field">
          <label className="field-label">Password</label>
          <div className="field-input">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#857B6D" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="11" width="16" height="10" rx="2"/>
              <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
            </svg>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
            <button
              type="button"
              className="field-input-eye"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword(v => !v)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#857B6D" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <label className="login-remember">
        <input
          type="checkbox"
          checked={remember}
          onChange={e => setRemember(e.target.checked)}
        />
        <span className="login-remember-box" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
        </span>
        <span className="login-remember-text">Keep me logged in until I sign out</span>
      </label>

      <button className="cta-saffron" type="submit" disabled={loading || !allFilled}>
        {loading ? (
          <>
            <span className="cta-spinner" aria-hidden="true"></span>
            <span>Signing in…</span>
          </>
        ) : (
          <>
            <span>Sign in</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F5F7FB" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 5l7 7-7 7"/>
            </svg>
          </>
        )}
      </button>

      {/* Mock fingerprint button — MOBILE ONLY (hidden ≥769px via CSS).
          Purely a styled alternate Sign-in trigger; no real biometrics, so it
          works on every phone regardless of hardware. */}
      <button
        className={`fp-mock fp-${phase}`}
        type="submit"
        disabled={loading || !allFilled}
        aria-label="Sign in with fingerprint"
      >
        <span className="fp-mock-ring">
          <span className="fp-mock-scan" aria-hidden="true"></span>
          <svg className="fp-print" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 11a1 1 0 0 1 1 1c0 3-.4 5-1.5 6.5"/>
            <path d="M5.5 13.5C5 12.5 5 11 5.5 9.5"/>
            <path d="M8.5 7.5A5 5 0 0 1 17 11c0 1.5 0 3-.5 4.5"/>
            <path d="M8 12a4 4 0 0 1 8 0c0 2.5-.3 4.3-1 6"/>
            <path d="M3.5 9a9 9 0 0 1 15-2.5"/>
            <path d="M9 18.8C9.6 17 10 14.6 10 12a2 2 0 0 1 4 0"/>
          </svg>
          <svg className="fp-check" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
        </span>
        <span className="fp-mock-label">
          {phase === 'scanning' ? 'Reading…'
            : phase === 'success' || phase === 'entering' ? 'Welcome back'
            : phase === 'error' ? 'Try again'
            : 'Tap to sign in'}
        </span>
      </button>

      <div className="login-create" style={{ marginBottom: 8 }}>
        <Link to="/forgot-password" style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          Forgot password?
        </Link>
      </div>
      <div className="login-create">
        New restaurant? <Link to="/register">Create an account <span className="arrow">→</span></Link>
      </div>
    </form>
  );

  return (
    <div className="login-page">
      {/* ─────────── Splash intro ─────────── */}
      {/* Full-screen navy with VP monogram at center. Fades out after the
          monogram has had its presence moment, revealing the login layout
          underneath whose content is staggering in synchronously. */}
      <div className="login-intro" aria-hidden="true">
        <VPMonogram size={280} />
      </div>

      {/* ─────────── Desktop layout ─────────── */}
      <div className="login-desktop">
        {/* Noir brand panel */}
        <aside className="noir-panel">
          <div className="noir-panel-vignette"></div>
          <div className="noir-panel-crescent"></div>

          <div className="noir-panel-top">
            <Wordmark />
            <StampLine time={time} location="" date={date} />
          </div>

          <div className="noir-panel-center">
            <div className="noir-panel-monogram"><VPMonogram size={280} /></div>
            <div className="noir-panel-headline">
              <h2 className="noir-panel-h2">
                Run your restaurant.<br />
                <span className="italic">Quietly.</span>
              </h2>
            </div>
          </div>

          <div className="quote-marquee">
            {QUOTES.map(([before, em, after], i) => (
              <div key={i} className="quote-card" style={{ animationDelay: `${i * 7}s` }}>
                <div className="quote-text">
                  {before}<span className="em">{em}</span>{after}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* Form side */}
        <main className="form-side">
          <svg className="form-side-deco" width="120" height="120" viewBox="0 0 120 120">
            <path d="M 0 60 L 120 60 M 60 0 L 60 120" stroke="#E6DFD2" strokeWidth="0.5" strokeDasharray="2 4"/>
            <circle cx="60" cy="60" r="40" stroke="#E6DFD2" strokeWidth="0.5" fill="none"/>
          </svg>
          <div className="form-card">
            <div className="form-card-rule"></div>
            {formBlock}
          </div>
        </main>
      </div>

      {/* ─────────── Mobile layout ─────────── */}
      <div className="login-mobile">
        <div className="mobile-hero">
          <div className="mobile-hero-glow"></div>
          <div className="mobile-hero-top">
            <Wordmark />
          </div>

          <div className="mobile-hero-center">
            <VPMonogram size={130} />
            <div className="mobile-hero-h2">
              Run your restaurant.<br />
              <span className="italic">Quietly.</span>
            </div>
          </div>

          <div className="mobile-hero-bottom">
            <StampLine time={time} location="" />
          </div>
        </div>

        <div className="mobile-sheet">
          <div className="mobile-sheet-handle"></div>
          {formBlock}
        </div>

        {/* Navy "entering dashboard" wipe — plays after a successful sign-in
            on mobile, over the already in-flight dashboard load. */}
        <div className={`mobile-enter${phase === 'entering' ? ' show' : ''}`} aria-hidden="true">
          <VPMonogram size={150} />
        </div>
      </div>
    </div>
  );
}
